import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Cog, Plus, Trash2, Pencil, Upload, Download, FileSpreadsheet, Save, X, Search, History } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API_BASE } from "@/lib/api";

export default function MachinesMaster() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role === "admin" || user?.role === "admin_addl");
  const [machines, setMachines] = useState([]);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState(null); // machine being edited
  const [newForm, setNewForm] = useState({ name: "", operations: "" });
  const [uploadText, setUploadText] = useState("");
  const [uploadMode, setUploadMode] = useState("merge");
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [xlsxFile, setXlsxFile] = useState(null);
  const [xlsxBusy, setXlsxBusy] = useState(false);
  // Audit log
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState([]);

  const load = async () => { const { data } = await api.get("/masters/machines"); setMachines(data); };
  useEffect(() => { load(); }, []);

  const openAudit = async () => {
    try {
      const { data } = await api.get("/masters/machines/audit?limit=100");
      setAudit(data);
      setAuditOpen(true);
    } catch (e) { toast.error("Failed to load audit log"); }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return machines;
    const s = search.toLowerCase();
    return machines.filter((m) =>
      m.name.toLowerCase().includes(s) || (m.operations || []).some((o) => o.toLowerCase().includes(s))
    );
  }, [machines, search]);

  const addMachine = async () => {
    const name = newForm.name.trim();
    if (!name) { toast.error("Machine name required"); return; }
    const ops = newForm.operations.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    try {
      await api.post("/masters/machines", { name, operations: ops });
      toast.success(`Added ${name}`); setAddOpen(false); setNewForm({ name: "", operations: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };

  const saveEdit = async () => {
    const ops = editing.operations_text.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    try {
      await api.patch(`/masters/machines/${editing.id}`, { name: editing.name.trim(), operations: ops });
      toast.success("Machine updated"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const del = async (m) => {
    if (!confirm(`Delete machine "${m.name}" and all its operations?`)) return;
    try { await api.delete(`/masters/machines/${m.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  // CSV parsing
  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return [];
    // detect header
    const first = lines[0].toLowerCase();
    const hasHeader = first.includes("machine") && first.includes("operation");
    const rows = (hasHeader ? lines.slice(1) : lines).map((line) => {
      const parts = line.split(",").map((p) => p.replace(/^"|"$/g, "").trim());
      return { machine_name: parts[0] || "", operation_name: parts[1] || "" };
    }).filter((r) => r.machine_name);
    return rows;
  };

  const previewUpload = () => {
    const rows = parseCsv(uploadText);
    if (rows.length === 0) { toast.error("No valid rows parsed"); return; }
    setUploadPreview(rows);
  };

  const confirmUpload = async () => {
    try {
      const { data } = await api.post("/masters/machines/bulk_upload", { rows: uploadPreview, mode: uploadMode });
      setUploadResult(data);
      toast.success(`Imported: ${data.inserted} new, ${data.updated} updated`);
      load();
    } catch (e) { toast.error("Upload failed"); }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // .xlsx path — hand to server for parsing (openpyxl)
    if (/\.xlsx$/i.test(f.name)) {
      setXlsxFile(f);
      setUploadText(""); setUploadPreview(null); setUploadResult(null);
      return;
    }
    // CSV path — read + parse client-side
    setXlsxFile(null);
    const text = await f.text();
    setUploadText(text);
    setUploadPreview(null); setUploadResult(null);
  };

  const uploadXlsx = async () => {
    if (!xlsxFile) return;
    setXlsxBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", xlsxFile);
      fd.append("mode", uploadMode);
      const res = await fetch(`${API_BASE}/masters/machines/upload_xlsx`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      setUploadResult({ inserted: data.inserted, updated: data.updated, errors: [] });
      toast.success(`Imported ${data.inserted} new, ${data.updated} updated from Excel`);
      load();
    } catch (e) {
      toast.error(e.message || "Excel upload failed");
    } finally {
      setXlsxBusy(false);
    }
  };

  const downloadTemplate = () => {
    const csv = "machine_name,operation_name\nTable Operator,Marking\nTable Operator,Trimming\nSingle Needle Lock Stitch (SNLS),Shoulder join\nSingle Needle Lock Stitch (SNLS),Side seam\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "machine-master-template.csv"; a.click();
  };

  const exportCurrent = () => {
    const rows = [["machine_name", "operation_name"]];
    machines.forEach((m) => {
      if (m.operations.length === 0) rows.push([m.name, ""]);
      else m.operations.forEach((op) => rows.push([m.name, op]));
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `machine-master-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    toast.success("Exported current machine master");
  };

  return (
    <div className="space-y-6" data-testid="machines-master-page">
      {/* Header */}
      <div>
        <button onClick={() => nav("/bulletins")} className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-500 hover:text-slate-800 mb-3">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Bulletins
        </button>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <div className="text-[11.5px] font-mono tracking-widest text-brand uppercase inline-flex items-center gap-1.5"><Cog className="h-3.5 w-3.5" /> Machine Master</div>
            <h1 className="mt-1 font-heading font-bold text-3xl tracking-tight">Machines & Operations</h1>
            <p className="text-slate-500 text-[14px] mt-1">Manage the machine list and their allowed operations. This master feeds every Operation Bulletin dropdown.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isAdmin && (
              <Button variant="outline" onClick={openAudit} data-testid="audit-log-btn"><History className="h-4 w-4 mr-2" /> Audit Log</Button>
            )}
            <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn"><Download className="h-4 w-4 mr-2" /> Template</Button>
            <Button variant="outline" onClick={exportCurrent} data-testid="export-machines-btn"><Download className="h-4 w-4 mr-2" /> Export Current</Button>
            {isAdmin && (
              <>
                <Dialog open={uploadOpen} onOpenChange={(v) => { setUploadOpen(v); if (!v) { setUploadText(""); setUploadPreview(null); setUploadResult(null); setXlsxFile(null); }}}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-testid="upload-machines-btn"><Upload className="h-4 w-4 mr-2" /> Upload CSV / XLSX</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Upload Machine Master (CSV / Excel)</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="text-[13px] text-slate-600">
                        Accepts <span className="font-mono bg-slate-100 px-1 rounded">.csv</span> (columns: machine_name, operation_name) OR <span className="font-mono bg-slate-100 px-1 rounded">.xlsx</span> with one sheet per machine and operations in column B (like <span className="italic">Operations_By_Machine_Tabs.xlsx</span>). Operations are auto-sorted alphabetically on import.
                      </div>

                      <div className="flex items-center gap-3">
                        <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer px-3 py-2 border border-slate-200 rounded-md hover:bg-slate-50">
                          <FileSpreadsheet className="h-4 w-4" />
                          <span>Choose CSV or XLSX file</span>
                          <input type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={handleFile} className="hidden" data-testid="csv-file-input" />
                        </label>
                        <span className="text-[12px] text-slate-400">or paste CSV below</span>
                      </div>

                      {xlsxFile && (
                        <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-[13px] text-emerald-900 flex items-center gap-2" data-testid="xlsx-file-info">
                          <FileSpreadsheet className="h-4 w-4 shrink-0" />
                          <span className="flex-1"><span className="font-semibold">{xlsxFile.name}</span> ({(xlsxFile.size / 1024).toFixed(1)} KB) — server will parse tabs on import</span>
                          <button onClick={() => setXlsxFile(null)} className="text-emerald-700 hover:text-emerald-900"><X className="h-4 w-4" /></button>
                        </div>
                      )}

                      {!xlsxFile && (
                        <Textarea
                          rows={6}
                          value={uploadText}
                          onChange={(e) => { setUploadText(e.target.value); setUploadPreview(null); setUploadResult(null); }}
                          placeholder="machine_name,operation_name&#10;Table Operation,Marking&#10;Table Operation,Trimming&#10;SNLS,Shoulder join"
                          data-testid="csv-textarea"
                          className="font-mono text-[12.5px]"
                        />
                      )}

                      <div>
                        <div className="text-[12px] font-semibold text-slate-600 uppercase tracking-wide mb-2">Import mode</div>
                        <RadioGroup value={uploadMode} onValueChange={setUploadMode} className="flex gap-6" data-testid="upload-mode-group">
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="merge" id="merge" data-testid="mode-merge" />
                            <Label htmlFor="merge" className="text-[13px] font-normal cursor-pointer">Merge (add to existing operations)</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="replace" id="replace" data-testid="mode-replace" />
                            <Label htmlFor="replace" className="text-[13px] font-normal cursor-pointer">Replace (wipe all & re-import)</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {!xlsxFile && !uploadPreview && (
                        <Button variant="outline" onClick={previewUpload} disabled={!uploadText.trim()} data-testid="preview-upload-btn">
                          <Search className="h-4 w-4 mr-2" /> Preview
                        </Button>
                      )}

                      {uploadPreview && !xlsxFile && (
                        <div className="border border-slate-200 rounded-md overflow-hidden">
                          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[12.5px] flex items-center justify-between">
                            <span><span className="font-semibold">{uploadPreview.length}</span> rows · <span className="font-semibold">{new Set(uploadPreview.map((r) => r.machine_name)).size}</span> unique machines</span>
                            <button onClick={() => setUploadPreview(null)} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
                          </div>
                          <div className="max-h-52 overflow-y-auto">
                            <table className="opsiq-table">
                              <thead><tr><th>Machine</th><th>Operation</th></tr></thead>
                              <tbody>
                                {uploadPreview.slice(0, 100).map((r, i) => (
                                  <tr key={i}><td>{r.machine_name}</td><td className="text-slate-600">{r.operation_name || <span className="text-slate-400">(no operation)</span>}</td></tr>
                                ))}
                              </tbody>
                            </table>
                            {uploadPreview.length > 100 && <div className="p-3 text-center text-[12px] text-slate-500">…and {uploadPreview.length - 100} more rows</div>}
                          </div>
                        </div>
                      )}

                      {uploadResult && (
                        <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-[13px] text-emerald-800" data-testid="upload-result">
                          Imported: <span className="font-semibold">{uploadResult.inserted}</span> new machines, <span className="font-semibold">{uploadResult.updated}</span> updated
                          {uploadResult.errors?.length > 0 && <span> · {uploadResult.errors.length} error(s)</span>}
                        </div>
                      )}
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setUploadOpen(false)}>Close</Button>
                      {xlsxFile ? (
                        <Button onClick={uploadXlsx} disabled={xlsxBusy} className="bg-brand hover:bg-brand-dark text-white" data-testid="confirm-xlsx-btn">
                          <Upload className="h-4 w-4 mr-2" /> {xlsxBusy ? "Importing…" : "Import Excel"}
                        </Button>
                      ) : (
                        <Button onClick={confirmUpload} disabled={!uploadPreview} className="bg-brand hover:bg-brand-dark text-white" data-testid="confirm-upload-btn">
                          <Upload className="h-4 w-4 mr-2" /> Confirm Import
                        </Button>
                      )}
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Dialog open={addOpen} onOpenChange={setAddOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-brand hover:bg-brand-dark text-white" data-testid="add-machine-btn"><Plus className="h-4 w-4 mr-2" /> Add Machine</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Add Machine</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <div>
                        <label className="text-[12px] font-semibold text-slate-600 uppercase tracking-wide">Machine Name</label>
                        <Input value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} placeholder="e.g. Chain Stitch" data-testid="new-machine-name" className="mt-1" />
                      </div>
                      <div>
                        <label className="text-[12px] font-semibold text-slate-600 uppercase tracking-wide">Operations</label>
                        <div className="text-[11px] text-slate-500 mb-1">One per line, or comma-separated</div>
                        <Textarea rows={5} value={newForm.operations} onChange={(e) => setNewForm({ ...newForm, operations: e.target.value })} placeholder="Chain hem&#10;Waistband finish" data-testid="new-machine-ops" />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
                      <Button onClick={addMachine} className="bg-brand hover:bg-brand-dark text-white" data-testid="new-machine-confirm">Add</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Search + stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card className="p-4 rounded-md border-slate-200 bg-white md:col-span-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search machine or operation..."
              data-testid="machines-search"
              className="w-full h-10 pl-9 pr-3 rounded-md bg-slate-50 border border-slate-200 text-sm outline-none focus:bg-white focus:border-slate-300"
            />
          </div>
        </Card>
        <Card className="p-4 rounded-md border-slate-200 bg-white">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Machines</div>
          <div className="mt-1 font-heading font-bold text-2xl">{machines.length}</div>
        </Card>
        <Card className="p-4 rounded-md border-slate-200 bg-white">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Operations</div>
          <div className="mt-1 font-heading font-bold text-2xl">{machines.reduce((s, m) => s + m.operations.length, 0)}</div>
        </Card>
      </div>

      {/* Machines list */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map((m) => {
          const isEditing = editing?.id === m.id;
          return (
            <Card key={m.id} data-testid={`machine-card-${m.name}`} className="border-slate-200 rounded-md bg-white p-5">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  {isEditing ? (
                    <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="font-heading font-bold text-[16px]" />
                  ) : (
                    <div className="font-heading font-bold text-[16px] tracking-tight text-slate-900">{m.name}</div>
                  )}
                  <div className="text-[11.5px] text-slate-500 mt-0.5">{m.operations.length} operation{m.operations.length !== 1 ? "s" : ""}</div>
                </div>
                {isAdmin && !isEditing && (
                  <div className="flex gap-1">
                    <button onClick={() => setEditing({ ...m, operations_text: m.operations.join("\n") })} className="h-8 w-8 rounded hover:bg-slate-100 flex items-center justify-center text-slate-500" data-testid={`edit-machine-${m.name}`}><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => del(m)} className="h-8 w-8 rounded hover:bg-red-50 flex items-center justify-center text-slate-400 hover:text-red-600" data-testid={`delete-machine-${m.name}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                )}
                {isEditing && (
                  <div className="flex gap-1">
                    <button onClick={saveEdit} className="h-8 w-8 rounded bg-brand hover:bg-brand-dark text-white flex items-center justify-center" data-testid={`save-machine-${m.name}`}><Save className="h-3.5 w-3.5" /></button>
                    <button onClick={() => setEditing(null)} className="h-8 w-8 rounded hover:bg-slate-100 flex items-center justify-center text-slate-500"><X className="h-3.5 w-3.5" /></button>
                  </div>
                )}
              </div>

              {isEditing ? (
                <Textarea rows={6} value={editing.operations_text} onChange={(e) => setEditing({ ...editing, operations_text: e.target.value })} placeholder="One operation per line" className="font-mono text-[12.5px]" />
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {m.operations.map((op) => (
                    <span key={op} className="inline-flex items-center px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 text-[11.5px] font-medium border border-emerald-100">
                      {op}
                    </span>
                  ))}
                  {m.operations.length === 0 && <span className="text-[12px] text-slate-400 italic">No operations yet.</span>}
                </div>
              )}
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <div className="md:col-span-2 p-12 text-center text-sm text-slate-500 bg-white rounded-md border border-slate-200 border-dashed">
            No machines match your search.
          </div>
        )}
      </div>

      {/* Audit Log Dialog */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-brand" /> Machine Master · Audit Log</DialogTitle>
          </DialogHeader>
          <div className="text-[12.5px] text-slate-500 -mt-2">Every add, edit, delete and bulk import on the machine master is recorded. Most recent first.</div>
          <div className="max-h-[60vh] overflow-y-auto -mx-2">
            <table className="opsiq-table" data-testid="audit-table">
              <thead>
                <tr>
                  <th style={{ width: 130 }}>When</th>
                  <th style={{ width: 130 }}>Action</th>
                  <th>Machine</th>
                  <th>Change</th>
                  <th style={{ width: 170 }}>By</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((a) => {
                  const pill = {
                    create: "bg-emerald-50 text-emerald-700",
                    op_add: "bg-emerald-50 text-emerald-700",
                    update: "bg-blue-50 text-blue-700",
                    rename: "bg-blue-50 text-blue-700",
                    delete: "bg-red-50 text-red-700",
                    bulk_create: "bg-emerald-50 text-emerald-700",
                    bulk_merge: "bg-emerald-50 text-emerald-700",
                    bulk_replace: "bg-amber-50 text-amber-700",
                    bulk_replace_wipe: "bg-red-50 text-red-700",
                    xlsx_create: "bg-emerald-50 text-emerald-700",
                    xlsx_merge: "bg-emerald-50 text-emerald-700",
                    xlsx_replace: "bg-amber-50 text-amber-700",
                    xlsx_replace_wipe: "bg-red-50 text-red-700",
                    cpm_update: "bg-purple-50 text-purple-700",
                  }[a.action] || "bg-slate-100 text-slate-700";
                  const added = a.added_ops?.length || 0;
                  const removed = a.removed_ops?.length || 0;
                  return (
                    <tr key={a.id}>
                      <td className="font-mono text-[11.5px] text-slate-500 whitespace-nowrap">{a.at?.slice(0,19).replace("T"," ")}</td>
                      <td><span className={`px-2 py-0.5 rounded text-[11px] font-medium ${pill}`}>{a.action}</span></td>
                      <td className="font-semibold">{a.machine_name || a.target || "—"}</td>
                      <td className="text-[12.5px] text-slate-600">
                        {a.action === "cpm_update" && a.before && a.after && (
                          <span>Machine CPM ₹{a.before.cpm_machine} → ₹{a.after.cpm_machine} · Table CPM ₹{a.before.cpm_table} → ₹{a.after.cpm_table}</span>
                        )}
                        {a.action === "rename" && a.before?.name !== a.after?.name && (
                          <span>Renamed <span className="font-mono">{a.before.name}</span> → <span className="font-mono">{a.after.name}</span></span>
                        )}
                        {(added > 0 || removed > 0) && (
                          <span>
                            {added > 0 && <span className="text-emerald-700">+{added} op{added>1?"s":""}</span>}
                            {added > 0 && removed > 0 && " · "}
                            {removed > 0 && <span className="text-red-700">−{removed} op{removed>1?"s":""}</span>}
                            {a.added_ops?.length > 0 && a.added_ops.length <= 3 && <span className="ml-1 text-slate-500">({a.added_ops.join(", ")})</span>}
                          </span>
                        )}
                        {a.action === "delete" && <span className="text-red-700">Deleted machine ({a.before?.operations?.length || 0} ops)</span>}
                        {a.action === "create" && <span className="text-emerald-700">Created ({a.after?.operations?.length || 0} ops)</span>}
                        {a.action.endsWith("_wipe") && <span className="text-red-700">All machines cleared</span>}
                      </td>
                      <td className="font-mono text-[12px]">{a.by}</td>
                    </tr>
                  );
                })}
                {audit.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-10 text-slate-500">No audit entries yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
